import { IAdvertiser } from '@/Interfaces/Advertiser.interface';
import { ICreative, ICreativeFilters } from '@/Interfaces/Creatives/Creative.interface';
import type { IParsedSegment, IParsedSingleSegment } from '@/Interfaces/Segments/Segment.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { mapApiCreativesToTableData, transformCreativeFilters } from '@/helpers/creativeLibrary';
import { getAdvertiserFilter } from '@/helpers/filters/filterMapping';
import { processApiSegmentsResponse, processSingleSegmentResponse } from '@/helpers/segments';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'axios';
import type { RootState } from '..';
import { archiveSegment, fetchCreatives, fetchSegment, fetchSegments } from '../api/segments.api';
import { selectRibeyeWithBeeswaxClients, selectRibeyeWithBeeswaxWhitelabels } from '../selectors/sharedApi.selectors';

enum Action {
  GetSegments = 'segmentsSlice/getSegments',
  GetSelectedSegment = 'segmentsSlice/getSelectedSegment',
  GetCreatives = 'segmentsSlice/getCreatives',
  UpdateActiveStatus = 'segmentsSlice/updateActiveStatus',
}

export const getSegments = createAsyncThunk<IParsedSegment[], IAbortSignalThunkArg>(
  Action.GetSegments,
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const {
      segments: { filters },
      me: { role },
    } = state;

    const whitelabels = selectRibeyeWithBeeswaxWhitelabels(state);
    const clients = selectRibeyeWithBeeswaxClients(state);

    const advertiserIds = getAdvertiserFilter(filters, role!, clients, whitelabels, 'value');

    const segments = await fetchSegments(advertiserIds, { active: filters.active }, payload?.signal);

    return processApiSegmentsResponse(segments);
  },
);

export const getSelectedSegment = createAsyncThunk<
  IParsedSingleSegment,
  IAbortSignalThunkArg & { id: IParsedSegment['id'] }
>(Action.GetSelectedSegment, async payload => {
  const segment = await fetchSegment(payload.id, payload.signal);

  return processSingleSegmentResponse(segment);
});

export const getCreatives = createAsyncThunk<
  { data: ICreative[]; total: number },
  IAbortSignalThunkArg & { advertiserId: IAdvertiser['id'] }
>(Action.GetCreatives, async (payload, { getState }) => {
  const {
    segments: { creativeFilters },
  } = getState() as RootState;
  const transformedFilters = transformCreativeFilters(creativeFilters as ICreativeFilters);
  const filters = { ...transformedFilters, advertiserIds: [payload.advertiserId] };
  const {
    data,
    meta: { total },
  } = await fetchCreatives(filters, payload.signal);

  return { data: mapApiCreativesToTableData(data), total };
});

export const updateActiveStatus = createAsyncThunk<{ id: number }, { id: number; active: boolean }>(
  Action.UpdateActiveStatus,
  async (payload, { rejectWithValue }) => {
    const response = await archiveSegment(payload);

    if (response.status < HttpStatusCode.Ok || response.status >= HttpStatusCode.MultipleChoices) {
      return rejectWithValue(response.statusText || 'Failed to archive segment');
    }

    return { id: payload.id };
  },
);
